import { useCallback, useRef } from 'react';
import { track } from '@lf/analytics-events';
import { updateVideoProgress } from '../../api/lfApiVideo';
import { parseSearchParam } from './parseSearchParam';
import { playEarnSound } from './playEarnSound';

function logVideoAdvanced(videoId, fromState, toState) {
  const { studyPlanId, lessonId } = parseSearchParam();
  track('video_advanced', {
    video_id: videoId,
    from_state: fromState,
    to_state: toState,
    source: studyPlanId ? 'study_plan' : 'direct',
    lesson_id: lessonId || undefined,
    study_plan_id: studyPlanId || undefined,
  });
}

function _firstQna(qnaState) {
  return qnaState.videoDetail.qna && qnaState.videoDetail.qna[0];
}

function _quizContextParams() {
  const { studyPlanId, lessonId } = parseSearchParam();
  const params = {};
  if (lessonId) params.lesson_id = lessonId;
  if (studyPlanId) params.study_plan_id = studyPlanId;
  return params;
}

/**
 * Bundles the user-driven event handlers (next button, step selection,
 * answer scoring) that mutate qnaState + stars.
 */
export function useVideoActions({
  videoId, channelId, qnaState, stars, setError, setBottomVisible, navigate,
}) {
  // Once-per-quiz-session guard. The quiz "starts" the first time the
  // user crosses into selectedComponentIndex >= 1; the corresponding
  // chat_id is the first qna entry's chat_id.
  const quizStartedRef = useRef(false);
  const quizCompletedRef = useRef(false);

  const onNext = useCallback(async () => {
    const fromState = qnaState.currentState();
    const { studyPlanId, lessonId, topicId } = parseSearchParam();

    if (fromState === 'LAST_QNA') {
      logVideoAdvanced(videoId, fromState, 'COMPLETED');
      // Quiz session ended by user advancing past the last question.
      const lastQna = qnaState.videoDetail.qna[qnaState.videoDetail.qna.length - 1]
        || _firstQna(qnaState);
      if (lastQna?.chat_id && !quizCompletedRef.current) {
        quizCompletedRef.current = true;
        track('quiz_completed', {
          chat_id: lastQna.chat_id,
          step_count: qnaState.videoDetail.qna.length,
          ended_by: 'user',
        });
      }
      await updateVideoProgress(
        videoId, channelId, studyPlanId, lessonId, topicId, 100, setError);
      navigate(`/feed?sp=${studyPlanId}`);
      return;
    }

    qnaState.setIsAskingQuestion(false);
    const newVal = qnaState.selectedComponentIndex < 1
      ? 1
      : qnaState.selectedComponentIndex + 1;

    // First crossing into the quiz fires `quiz_started`. Use a ref to
    // guarantee at-most-once per mount; the quiz remains in "started"
    // until quiz_completed fires from LAST_QNA above.
    if (newVal === 1 && !quizStartedRef.current) {
      const first = _firstQna(qnaState);
      if (first?.chat_id) {
        quizStartedRef.current = true;
        track('quiz_started', {
          video_id: videoId,
          chat_id: first.chat_id,
          question_count: qnaState.videoDetail.qna.length,
          ..._quizContextParams(),
        });
      }
    }

    qnaState.setSelectedComponentIndex(newVal);

    const nextQna = qnaState.videoDetail.qna[newVal - 1];
    let toState = fromState;
    if (newVal > 0 && nextQna) {
      if (stars.starCount > 0) {
        stars.claimStars();
        playEarnSound();
      }
      qnaState.setEoc(nextQna.eoc);

      toState = newVal === qnaState.videoDetail.qna.length
        ? 'LAST_QNA'
        : (newVal === 1 ? 'FIRST_QNA' : 'QNA');

      // Each transition into a new quiz step is a "step viewed" signal.
      track('quiz_step_viewed', {
        chat_id: nextQna.chat_id,
        step_index: newVal - 1,
        is_multiple_choice: nextQna.multiple_choices && nextQna.multiple_choices.length > 0 ? 1 : 0,
        is_hint: 0,
      });

      await updateVideoProgress(
        videoId, channelId, studyPlanId, lessonId, topicId,
        Math.round((newVal / qnaState.videoDetail.qna.length) * 100),
        setError,
      );
    }

    logVideoAdvanced(videoId, fromState, toState);
    setBottomVisible(true);
  }, [channelId, navigate, qnaState, setBottomVisible, setError, stars, videoId]);

  const onSelectStep = useCallback((index) => {
    const nextIndex = index + 1;
    qnaState.setSelectedComponentIndex(nextIndex);
    const item = qnaState.videoDetail.qna[index];
    if (item) {
      qnaState.setEoc(item.eoc);
      track('quiz_step_viewed', {
        chat_id: item.chat_id,
        step_index: index,
        is_multiple_choice: item.multiple_choices && item.multiple_choices.length > 0 ? 1 : 0,
        is_hint: 0,
      });
    }
    setBottomVisible(true);
  }, [qnaState, setBottomVisible]);

  const onAnswerEoc = useCallback((isEoc, awardedStars, triggerConfetti) => {
    const state = qnaState.currentState();
    if (qnaState.selectedComponentIndex === qnaState.videoDetail.qna.length
        && triggerConfetti) {
      stars.setShowConfetti(true);
    }

    const stepIndex = qnaState.selectedComponentIndex - 1;
    const currentQna = qnaState.videoDetail.qna[stepIndex];
    if (currentQna) {
      currentQna.eoc = isEoc;
      // Only fire on the actual answer event (isEoc=true with awarded
      // stars, or isEoc=false with awardedStars=0 meaning a wrong
      // answer that re-armed the timeout). The pure timeout-rearm path
      // (isEoc=true awardedStars=0) is also called and is fine to log.
      track('quiz_question_answered', {
        chat_id: currentQna.chat_id,
        step_index: stepIndex,
        is_correct: isEoc && awardedStars > 0 ? 1 : 0,
        attempt_index: 0,
      });
    }
    qnaState.setEoc(isEoc);
    stars.setStarCount(awardedStars);

    // The last question has no "claim points" button so award immediately.
    // The "tada" sound from the confetti effect substitutes for "earn".
    if (state === 'LAST_QNA') {
      stars.setTotalStarCount((prev) => prev + awardedStars);
    }
  }, [qnaState, stars]);

  return { onNext, onSelectStep, onAnswerEoc };
}
